import { Cheque } from '@baemingo/table-events/dist/types';
import { DateTime } from 'luxon';
import { useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import TableItem from '../../Components/TableItem';
import { VenueData_venue_floorPlans_tables } from '../../__queries__';

const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  padding: 16px;
`;

interface Props {
  tables: VenueData_venue_floorPlans_tables[];
  allCheques: Cheque[];
  selectedTable: string | undefined;
  selectedTableIds?: string[]; //Used for selected tables in table select modal (bookings move to table)
  setSelectedTable: (id: string, name: string, coverage?: number) => void;
  onTableDoubleClick?: (id: string, name: string, coverage?: number) => void;
  showOnlyOpen: boolean;
}

const Grid: React.FunctionComponent<Props> = ({
  tables,
  allCheques,
  selectedTable,
  selectedTableIds,
  setSelectedTable,
  onTableDoubleClick,
  showOnlyOpen,
}) => {
  const companyTimezone = useSelector((state) => state.companyTimeZone);
  const [{ showSeatingTimeInTSR }] = useStorage();
  const [timeCounter, setTimeCounter] = useState(0);
  /*   const isFirstRender = useRef(true);
   */ const [tableActivityMap, setTableActivityMap] = useState<
    Record<string, { activeAt: string | false; createdAt: string | false }>
  >({});

  let tablesArr = tables?.sort(function(a: any, b: any) {
    return a.code - b.code;
  });

  if (showOnlyOpen) {
    tablesArr = tablesArr.filter((table: VenueData_venue_floorPlans_tables) =>
      allCheques.some((item: any) => item.tableId === table.id),
    );
  }

  useEffect(() => {
    const intervalId = setInterval(() => setTimeCounter((counter) => counter + 1), 60000);
    return () => clearInterval(intervalId);
  }, [selectedTable]);

  useEffect(() => {
    const newActivityMap: Record<
      string,
      { activeAt: string | false; createdAt: string | false }
    > = {};

    /*     const tableActivity = () => { */
    tablesArr?.forEach((table) => {
      const tableCheques = allCheques.filter((item) => item.tableId === table.id);
      if (tableCheques.length > 0) {
        // Get the first cheque's ID for this table (or whichever is relevant)
        const cheque = tableCheques[0];
        const activeAt = cheque?.activeAt || false;
        const createdAt = cheque.createdAt || false;
        if (activeAt && createdAt) {
          newActivityMap[table.id] = { activeAt, createdAt };
        }
      }
    });

    setTableActivityMap(newActivityMap);
    /*     };
     */
    /*     if (isFirstRender.current) {
      isFirstRender.current = false;
      const timer = setTimeout(() => {
        tableActivity();
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      tableActivity();
    } */
  }, [timeCounter, selectedTable]);

  const { staleTables, moderateTables } = useMemo(() => {
    const now = DateTime.now().setZone(companyTimezone);
    const tableActivityDiffs: Array<{ tableId: string; diffMinutes: number }> = [];

    // Calculate time differences for each table
    Object.entries(tableActivityMap).forEach(([tableId, data]) => {
      const { activeAt, createdAt } = data;

      if (activeAt && createdAt) {
        const activeTime = DateTime.fromISO(activeAt);
        const createdTime = DateTime.fromISO(createdAt);

        // Ensure we have valid dates
        if (!activeTime.isValid || !createdTime.isValid) return;

        // Calculate time differences
        const activeTimeDiff = now.diff(activeTime, 'minutes').minutes;
        const createdTimeDiff = now.diff(createdTime, 'minutes').minutes;

        // Only include tables with:
        // 1. Cheques created within last 4 hours
        // 2. Activity between 15 minutes and 2 hours ago
        if (createdTimeDiff <= 240 && activeTimeDiff >= 5 && activeTimeDiff <= 120) {
          tableActivityDiffs.push({
            tableId,
            diffMinutes: activeTimeDiff,
          });
        }
      }
    });

    // Sort tables by activity difference (oldest first)
    tableActivityDiffs.sort((a, b) => b.diffMinutes - a.diffMinutes);

    // Calculate how many tables to include in each category, include at least 1 table or round up
    const totalFilteredTables = tableActivityDiffs.length;
    const staleCount = Math.max(1, Math.round(totalFilteredTables * 0.05));
    const moderateCount = Math.max(1, Math.round(totalFilteredTables * 0.05));

    // Get the tables for each category
    const staleTableIds = tableActivityDiffs.slice(0, staleCount).map((item) => item.tableId);
    const moderateTableIds = tableActivityDiffs
      .slice(staleCount, staleCount + moderateCount)
      .map((item) => item.tableId);

    return {
      staleTables: staleTableIds,
      moderateTables: moderateTableIds,
    };
  }, [tableActivityMap, companyTimezone]);

  // Determine activity status for a table
  const getActiveStatus = (tableId: string) => {
    if (staleTables.includes(tableId)) return 'stale';
    if (moderateTables.includes(tableId)) return 'moderate';
    return 'none';
  };

  return (
    <Wrapper>
      {tablesArr?.map((table) => {
        const tableCheques = allCheques.filter((item) => item.tableId === table.id);
        const activityStatus = getActiveStatus(table.id);

        const nrOfPeople =
          tableCheques.length > 0
            ? Math.max(...tableCheques.map((item) => (item.coverage ? item.coverage : 0)))
            : undefined;

        // Calculate seating time from the oldest cheque's createdAt (rounded to the minute)
        let seatingTime: string | undefined;
        if (showSeatingTimeInTSR && tableCheques.length > 0) {
          const oldestCheque = tableCheques.reduce((a, b) =>
            b.createdAt < a.createdAt ? b : a,
          );
          if (oldestCheque.createdAt) {
            const diff = DateTime.now()
              .setZone(companyTimezone)
              .startOf('minute')
              .diff(DateTime.fromISO(oldestCheque.createdAt).startOf('minute'), ['hours', 'minutes']);
            const hours = Math.floor(diff.hours);
            const minutes = Math.floor(diff.minutes);
            seatingTime = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
          }
        }

        return (
          <TableItem
            key={table.id}
            id={table.id}
            active={tableCheques.length > 0}
            activityStatus={activityStatus}
            hasPrinted={tableCheques.some((c) => c.printed)}
            courseStageId={tableCheques.find((c) => c.stage)?.stage ?? null}
            selected={
              selectedTableIds ? selectedTableIds.includes(table.id) : table.id === selectedTable
            }
            tableName={table.code}
            peopleCount={nrOfPeople}
            setSelectedTable={setSelectedTable}
            onDoubleClick={onTableDoubleClick}
            grid={true}
            seatingTime={seatingTime}
          />
        );
      })}
    </Wrapper>
  );
};

export default React.memo(Grid);
